import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface MarketDataRow {
  ticker: string;
  iv: number | null;
  expectedMove: number | null;
  totalPointsMoved: number | null;
  totalRange: number | null;
  date: string;
}

export interface LevelsRow {
  ticker: string;
  low: number | null;
  mid: number | null;
  high: number | null;
  sd2Low: number | null;
  sd2High: number | null;
  date: string;
}


export interface AiStockRow {
  layer: string;
  layerGroup: string;
  layerTheme: string;
  ticker: string;
  company: string;
  marketCap: string;
  ltp: number | null;
  yearLow: number | null;
  yearHigh: number | null;
  oneWeekPct: number | null;
  oneMonthPct: number | null;
  threeMonthPct: number | null;
  sixMonthPct: number | null;
  oneYearPct: number | null;
  twoYearPct: number | null;
  threeYearPct: number | null;
  fourYearPct: number | null;
}

export interface StockSectorRow {
  symbol: string;
  sector: string;
  marketCap: string | number | null;
  ltp: number | null;
  yearLow: number | null;
  yearHigh: number | null;
  oneWeekPct: number | null;
  oneMonthPct: number | null;
  threeMonthPct: number | null;
  sixMonthPct: number | null;
  oneYearPct: number | null;
  twoYearPct: number | null;
  threeYearPct: number | null;
  fourYearPct: number | null;
}

export interface StocksRow {
  ticker: string;
  low: number | null;
  mid: number | null;
  high: number | null;
  s2: number | null;
  r2: number | null;
  marketCap: string;
  fridayClose: number | null;
  iv: number | null;
}

export interface RangeEstimationCsvRow {
  ticker: string;
  iv: number | null;
  expectedMove: number | null;
  previousClose: number | null;
  currentClose: number | null;
  closeToCloseMove: number | null;
  low: number | null;
  high: number | null;
  range: number | null;
  date: string;
}

@Injectable({ providedIn: 'root' })
export class CsvDataService {
  constructor(private http: HttpClient) {}

  load(path: string): Observable<MarketDataRow[]> {
    return this.http.get(path, { responseType: 'text' }).pipe(
      map(text => this.parseIv(text))
    );
  }

  loadLevels(path: string): Observable<LevelsRow[]> {
    return this.http.get(path, { responseType: 'text' }).pipe(
      map(text => this.parseLevels(text))
    );
  }

  loadAiStocks(path: string): Observable<AiStockRow[]> {
    return this.http.get(path, { responseType: 'text' }).pipe(
      map(text => this.parseAiStocks(text))
    );
  }

  loadStockSectors(path: string): Observable<StockSectorRow[]> {
    return this.http.get(path, { responseType: 'text' }).pipe(
      map(text => this.parseStockSectors(text))
    );
  }

  loadStocks(path: string): Observable<StocksRow[]> {
    return this.http.get(path, { responseType: 'text' }).pipe(
      map(text => this.parseStocks(text))
    );
  }

  loadRangeEstimations(path: string): Observable<RangeEstimationCsvRow[]> {
    return this.http.get(path, { responseType: 'text' }).pipe(
      map(text => this.parseRangeEstimations(text))
    );
  }

  private parseIv(text: string): MarketDataRow[] {
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) return [];

    const headers = this.parseRow(lines[0]);
    const col = (row: string[], key: string) => row[headers.indexOf(key)]?.trim() ?? '';
    const num = (s: string): number | null => {
      if (!s) return null;
      // Remove thousands separators (commas) before parsing
      const cleaned = s.replace(/,/g, '');
      const n = parseFloat(cleaned);
      return isNaN(n) ? null : n;
    };

    return lines.slice(1).flatMap(line => {
      const cols = this.parseRow(line);
      const ticker = col(cols, 'ticker');
      if (!ticker) return [];
      return [{
        ticker,
        iv:               num(col(cols, 'IV')),
        expectedMove:     num(col(cols, 'Expected Move')),
        totalPointsMoved: num(col(cols, 'Total Points Moved from Close to Close')),
        totalRange:       num(col(cols, 'Total Range')),
        date:             col(cols, 'date'),
      }];
    });
  }

  private parseLevels(text: string): LevelsRow[] {
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) return [];

    const headers = this.parseRow(lines[0]);
    const col = (row: string[], key: string) => row[headers.indexOf(key)]?.trim() ?? '';
    const firstCol = (row: string[], keys: string[]) => {
      for (const key of keys) {
        const value = col(row, key);
        if (value) return value;
      }
      return '';
    };
    const num = (s: string): number | null => {
      if (!s) return null;
      // Remove thousands separators (commas) before parsing
      const cleaned = s.replace(/,/g, '');
      const n = parseFloat(cleaned);
      return isNaN(n) ? null : n;
    };

    return lines.slice(1).flatMap(line => {
      const cols = this.parseRow(line);
      const ticker = col(cols, 'Ticker');
      if (!ticker) return [];
      return [{
        ticker,
        low:    num(col(cols, 'low')),
        mid:    num(col(cols, 'mid')),
        high:   num(col(cols, 'high')),
        sd2Low: num(col(cols, 'SD2_low')),
        sd2High: num(col(cols, 'SD2_high')),
        date:   col(cols, 'Date'),
      }];
    });
  }

  private parseStocks(text: string): StocksRow[] {
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) return [];

    const headers = this.parseRow(lines[0]);
    const col = (row: string[], key: string) => row[headers.indexOf(key)]?.trim() ?? '';
    const num = (s: string): number | null => {
      if (!s) return null;
      const cleaned = s.replace(/,/g, '').replace(/%$/, '');
      const n = parseFloat(cleaned);
      return isNaN(n) ? null : n;
    };

    return lines.slice(1).flatMap(line => {
      const cols = this.parseRow(line);
      const ticker = col(cols, 'Ticker');
      if (!ticker) return [];
      return [{
        ticker,
        low:         num(col(cols, 'low')),
        mid:         num(col(cols, 'mid')),
        high:        num(col(cols, 'high')),
        s2:          num(col(cols, 'S2')),
        r2:          num(col(cols, 'R2')),
        marketCap:   col(cols, 'Market Cap'),
        fridayClose: num(col(cols, 'Friday Closing Price')),
        iv:          num(col(cols, 'IV')),
      }];
    });
  }

  private parseRangeEstimations(text: string): RangeEstimationCsvRow[] {
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) return [];

    const headers = this.parseRow(lines[0]).map(header => header.trim());
    const col = (row: string[], key: string) => row[headers.indexOf(key)]?.trim() ?? '';
    const firstCol = (row: string[], keys: string[]) => {
      for (const key of keys) {
        const value = col(row, key);
        if (value) return value;
      }
      return '';
    };
    const num = (s: string): number | null => {
      if (!s) return null;
      const n = parseFloat(s.replace(/,/g, ''));
      return isNaN(n) ? null : n;
    };

    return lines.slice(1).flatMap(line => {
      const cols = this.parseRow(line);
      const ticker = col(cols, 'ticker').toUpperCase();
      const date = col(cols, 'date');
      if (!ticker || !date) return [];

      return [{
        ticker,
        iv: num(col(cols, 'iv')),
        expectedMove: num(col(cols, 'expectedMove')),
        previousClose: num(firstCol(cols, ['previousClose', 'previousDayClosingPrice', 'previousWeekClosingPrice'])),
        currentClose: num(firstCol(cols, ['currentClose', 'todaysClosingPrice', 'currentWeekClosingPrice'])),
        closeToCloseMove: num(firstCol(cols, ['closeToCloseMove', 'totalPointsMovedFromCloseToClose'])),
        low: num(firstCol(cols, ['low', 'todays_low', 'weekLow'])),
        high: num(firstCol(cols, ['high', 'todays_high', 'weekHigh'])),
        range: num(firstCol(cols, ['range', 'dayRange', 'weekRange'])),
        date,
      }];
    });
  }

  private parseAiStocks(text: string): AiStockRow[] {
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) return [];

    const headers = this.parseRow(lines[0]);
    const col = (row: string[], key: string) => row[headers.indexOf(key)]?.trim() ?? '';
    const num = (s: string): number | null => {
      if (!s) return null;
      const n = parseFloat(s.replace(/,/g, ''));
      return isNaN(n) ? null : n;
    };

    return lines.slice(1).flatMap(line => {
      const cols = this.parseRow(line);
      const ticker = col(cols, 'Ticker');
      if (!ticker) return [];
      const layer = col(cols, 'Layer');
      const layerMatch = layer.match(/^(Layer\s+\d+)\s*(.*)$/i);
      return [{
        layer,
        layerGroup: layerMatch?.[1]?.trim() ?? layer,
        layerTheme: layerMatch?.[2]?.trim() ?? '',
        ticker,
        company: col(cols, 'Company'),
        marketCap: col(cols, 'Market Cap'),
        ltp: null,
        yearLow: null,
        yearHigh: null,
        oneWeekPct: num(col(cols, '1 Week%')),
        oneMonthPct: num(col(cols, '1 Month%')),
        threeMonthPct: num(col(cols, '3 Month%')),
        sixMonthPct: num(col(cols, '6 Month%')),
        oneYearPct: num(col(cols, '1 Year%')),
        twoYearPct: num(col(cols, '2 Year%')),
        threeYearPct: num(col(cols, '3 Year%')),
        fourYearPct: num(col(cols, '4 Year%')),
      }];
    });
  }

  private parseStockSectors(text: string): StockSectorRow[] {
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) return [];

    const headers = this.parseRow(lines[0]);
    const col = (row: string[], key: string) => row[headers.indexOf(key)]?.trim() ?? '';
    const firstCol = (row: string[], keys: string[]) => {
      for (const key of keys) {
        const value = col(row, key);
        if (value) return value;
      }
      return '';
    };
    const num = (s: string): number | null => {
      if (!s) return null;
      const n = parseFloat(s.replace(/,/g, '').replace(/%/g, ''));
      return isNaN(n) ? null : n;
    };

    return lines.slice(1).flatMap(line => {
      const cols = this.parseRow(line);
      const symbol = col(cols, 'Symbol');
      if (!symbol) return [];
      return [{
        symbol,
        sector: col(cols, 'Sector'),
        marketCap: firstCol(cols, ['Market Cap', 'Marketcap', 'MarketCap']),
        ltp: null,
        yearLow: null,
        yearHigh: null,
        oneWeekPct: num(col(cols, '1 Week%')),
        oneMonthPct: num(col(cols, '1 Month%')),
        threeMonthPct: num(col(cols, '3 Month%')),
        sixMonthPct: num(col(cols, '6 Month%')),
        oneYearPct: num(col(cols, '1 Year%')),
        twoYearPct: num(col(cols, '2 Year%')),
        threeYearPct: num(col(cols, '3 Year%')),
        fourYearPct: num(col(cols, '4 Year%')),
      }];
    });
  }

  // Parse CSV row handling quoted fields
  private parseRow(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        // Toggle quote state
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        // End of field
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    // Push final field
    if (current || line.endsWith(',')) {
      result.push(current);
    }
    return result;
  }
}
